import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  createDataEntry,
  updateDataEntry,
  deleteDataEntry,
  uploadQuoteDocument,
  updateQuoteDocument,
  deleteQuoteDocument,
  uploadDraftBlDocument,
  updateDraftBlDocument,
  deleteDraftBlDocument,
  uploadRolloverDocument,
  updateRolloverDocument,
  deleteRolloverDocument,
  uploadCopyDocument,
  updateCopyDocument,
  deleteCopyDocument,
  uploadContainerImage,
  updateContainerImage,
  deleteContainerImage,
} from '../services/dataEntryService';
import { queryKeys } from '../constants/queryKeys';

// Helper to extract clean error message, handling 413 Payload Too Large
const getErrorMessage = (error, defaultMsg) => {
  if (error?.response?.status === 413) {
    return 'File size exceeds server limit. Please upload a file of 2.5 MB and below.';
  }
  return error?.response?.data?.message || defaultMsg;
};

// ─── Business Data Mutations ──────────────────────────────────────────────────

/**
 * useCreateDataEntry — POST /api/data-entry
 * Returns the created record (including its new `id`) for subsequent document uploads.
 */
export const useCreateDataEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createDataEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataEntry.all });
      toast.success('Data entry created successfully!');
    },
    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to create data entry.';
      toast.error(message);
    },
  });
};

/**
 * useUpdateDataEntry — PUT /api/data-entry/{id}
 * Usage: updateMutation.mutate({ id, data: formPayload })
 */
export const useUpdateDataEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => updateDataEntry(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataEntry.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dataEntry.detail(variables.id) });
      toast.success('Data entry updated successfully!');
    },
    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to update data entry.';
      toast.error(message);
    },
  });
};

/**
 * useDeleteDataEntry — DELETE /api/data-entry/{id}
 * Usage: deleteMutation.mutate(id)
 */
export const useDeleteDataEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteDataEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataEntry.all });
      toast.success('Data entry deleted successfully!');
    },
    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to delete data entry.';
      toast.error(message);
    },
  });
};

// ─── Quote Document Mutations ─────────────────────────────────────────────

/** Usage: mutate({ fileName, blNo, data: { fileName: file.name, base64 } }) */
export const useUploadQuoteDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileName, blNo, data }) => uploadQuoteDocument(fileName, blNo, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dataEntry', 'quoteDoc', variables.fileName, variables.blNo] });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Quote document uploaded!');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to upload quote document.')),
  });
};

/** Usage: mutate({ fileName, blNo, data: { fileName: file.name, base64 } }) */
export const useUpdateQuoteDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileName, blNo, data }) => updateQuoteDocument(fileName, blNo, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dataEntry', 'quoteDoc', variables.fileName, variables.blNo] });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Quote document updated!');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to update quote document.')),
  });
};

/** Usage: mutate({ fileName, blNo }) */
export const useDeleteQuoteDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileName, blNo }) => deleteQuoteDocument(fileName, blNo),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dataEntry', 'quoteDoc', variables.fileName, variables.blNo] });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Quote document deleted.');
    },
    onError: (error, variables) => {
      if (error?.response?.status === 404) {
        queryClient.invalidateQueries({ queryKey: ['dataEntry', 'quoteDoc', variables?.fileName, variables?.blNo] });
        queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      }
      toast.error(error?.response?.data?.message || 'Failed to delete quote document.');
    },
  });
};

// ─── Draft BL Document Mutations ──────────────────────────────────────────────

/** Usage: mutate({ fileName, blNo, data: { fileName: file.name, base64 } }) */
export const useUploadDraftBlDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileName, blNo, data }) => uploadDraftBlDocument(fileName, blNo, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dataEntry', 'draftBlDoc', variables.fileName, variables.blNo] });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Draft BL document uploaded!');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to upload draft BL document.')),
  });
};

/** Usage: mutate({ fileName, blNo, data: { fileName: file.name, base64 } }) */
export const useUpdateDraftBlDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileName, blNo, data }) => updateDraftBlDocument(fileName, blNo, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dataEntry', 'draftBlDoc', variables.fileName, variables.blNo] });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Draft BL document updated!');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to update draft BL document.')),
  });
};

/** Usage: mutate({ fileName, blNo }) */
export const useDeleteDraftBlDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileName, blNo }) => deleteDraftBlDocument(fileName, blNo),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dataEntry', 'draftBlDoc', variables.fileName, variables.blNo] });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Draft BL document deleted.');
    },
    onError: (error, variables) => {
      if (error?.response?.status === 404) {
        queryClient.invalidateQueries({ queryKey: ['dataEntry', 'draftBlDoc', variables?.fileName, variables?.blNo] });
        queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      }
      toast.error(error?.response?.data?.message || 'Failed to delete draft BL document.');
    },
  });
};

// ─── Rollover Document Mutations ──────────────────────────────────────────────

export const useUploadRolloverDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => uploadRolloverDocument(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataEntry.rolloverDoc(variables.id) });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Rollover document uploaded successfully!');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to upload rollover document.'));
    },
  });
};

export const useUpdateRolloverDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => updateRolloverDocument(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataEntry.rolloverDoc(variables.id) });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Rollover document updated!');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to update rollover document.'));
    },
  });
};

export const useDeleteRolloverDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteRolloverDocument(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataEntry.rolloverDoc(id) });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Rollover document deleted.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to delete rollover document.');
    },
  });
};

// ─── Copy Documents Mutations ─────────────────────────────────────────────

/** Usage: mutate({ fileName, blNo, data: { fileName: file.name, base64 } }) */
export const useUploadCopyDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileName, blNo, data }) => uploadCopyDocument(fileName, blNo, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dataEntry', 'copyDocsParams', variables.fileName, variables.blNo] });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Copy document uploaded!');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to upload copy document.')),
  });
};

/** Usage: mutate({ fileName, blNo, data }) */
export const useUpdateCopyDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileName, blNo, data }) => updateCopyDocument(fileName, blNo, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dataEntry', 'copyDocsParams', variables.fileName, variables.blNo] });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Copy document updated!');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to update copy document.')),
  });
};

/** Usage: mutate(documentId) */
export const useDeleteCopyDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId) => deleteCopyDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Copy document deleted.');
    },
    onError: (error) => toast.error(error?.response?.data?.message || 'Failed to delete copy document.'),
  });
};

// ─── Container Image Mutations ────────────────────────────────────────────────

/** Usage: mutate({ fileName, blNo, containerNo, data }) */
export const useUploadContainerImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileName, blNo, containerNo, data }) =>
      uploadContainerImage(fileName, blNo, containerNo, data),
    onSuccess: () => {
      // Invalidate the specific containerImage key so both useContainerImage
      // and useContainerImageByParams (which share this prefix) re-fetch immediately.
      queryClient.invalidateQueries({ queryKey: ['dataEntry', 'containerImage'] });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Container image uploaded!');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to upload container image.'));
    },
  });
};

/** Usage: mutate(id) */
export const useDeleteContainerImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteContainerImage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataEntry', 'containerImage'] });
      queryClient.invalidateQueries({ queryKey: ['dataEntry'] });
      toast.success('Container image deleted.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to delete container image.');
    },
  });
};
